import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  authKeysApi,
  getApiErrorMessage,
  queryKeys,
  authKeyQueries,
} from '@milliswift/api-client'
import {
  ApiKeyReveal,
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  PageSkeleton,
  TagInput,
  useToast,
  type Column,
} from '@milliswift/ui'
import { KeyRound, Plus } from 'lucide-react'
import { useState } from 'react'
import type { AuthKey } from '@milliswift/types'

/**
 * API keys for connecting the device agent.
 *
 * The raw key is displayed by ApiKeyReveal exactly once, gated behind a copy
 * acknowledgement, and never written to storage — the component holds it in
 * memory and this page holds it in state, which is dropped on unmount.
 */
export default function ApiKeys() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const keys = useQuery(authKeyQueries.list())

  const [createOpen, setCreateOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [cidrs, setCidrs] = useState<string[]>([])
  const [maxDevices, setMaxDevices] = useState('3')
  const [freshKey, setFreshKey] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<AuthKey | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.authKeys })

  const create = useMutation({
    mutationFn: () =>
      authKeysApi.create({
        label: label.trim(),
        allowed_cidrs: cidrs.length > 0 ? cidrs : undefined,
        max_devices: Number.parseInt(maxDevices, 10) || undefined,
      }),
    onSuccess: (response) => {
      setCreateOpen(false)
      setFreshKey(response.raw_key)
      setLabel('')
      setCidrs([])
      void invalidate()
    },
    onError: (error) => toast.error('Could not create the key', getApiErrorMessage(error)),
  })

  const revoke = useMutation({
    mutationFn: (keyId: string) => authKeysApi.revoke(keyId),
    onSuccess: () => {
      setRevokeTarget(null)
      toast.success('Key revoked')
      void invalidate()
    },
    onError: (error) => toast.error('Could not revoke', getApiErrorMessage(error)),
  })

  const regenerate = useMutation({
    mutationFn: (keyId: string) => authKeysApi.regenerate(keyId),
    onSuccess: (response) => {
      setFreshKey(response.raw_key)
      void invalidate()
    },
    onError: (error) => toast.error('Could not regenerate', getApiErrorMessage(error)),
  })

  const columns: Column<AuthKey>[] = [
    {
      key: 'label',
      header: 'Label',
      sortValue: (key) => key.label,
      cell: (key) => <span className="font-medium text-foreground">{key.label}</span>,
    },
    {
      key: 'prefix',
      header: 'Key',
      sortValue: (key) => key.prefix,
      cell: (key) => <code className="font-mono text-caption text-muted">{key.prefix}…</code>,
    },
    {
      key: 'created',
      header: 'Created',
      sortValue: (key) => key.created_at,
      cell: (key) => new Date(key.created_at).toLocaleDateString(),
    },
    {
      key: 'used',
      header: 'Last used',
      align: 'right',
      sortValue: (key) => key.last_used_at ?? '',
      cell: (key) =>
        key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : '—',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (key) => (
        <Badge tone={key.status === 'active' ? 'success' : 'neutral'} dot>
          {key.status === 'active' ? 'Active' : key.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (key) =>
        key.status === 'active' ? (
          <div className="flex justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              loading={regenerate.isPending && regenerate.variables === key.id}
              onClick={() => regenerate.mutate(key.id)}
            >
              Regenerate
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRevokeTarget(key)}>
              Revoke
            </Button>
          </div>
        ) : null,
    },
  ]

  if (keys.isPending) return <PageSkeleton />

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">API keys</h1>
          <p className="mt-1 text-sm text-muted">
            Keys authenticate the device agent when it connects. Keep one per environment.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
          Create key
        </Button>
      </div>

      {keys.isError ? (
        <Callout tone="danger" title="Could not load keys">
          {getApiErrorMessage(keys.error)}
        </Callout>
      ) : (keys.data ?? []).length === 0 ? (
        <EmptyState
          icon={<KeyRound />}
          title="No API keys yet"
          description="Create one to connect your first device. It is shown once, so save it somewhere safe."
          action={
            <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
              Create your first key
            </Button>
          }
        />
      ) : (
        <DataTable
          data={keys.data ?? []}
          columns={columns}
          getRowId={(key) => key.id}
          pageSize={10}
          cardTitle={(key) => key.label}
        />
      )}

      {/* Create */}
      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Create an API key"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={label.trim().length === 0}
              onClick={() => create.mutate()}
            >
              Generate key
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Label" required hint="Where this key is used, e.g. prod-server-01.">
            <Input
              value={label}
              onChange={(event) => setLabel(event.currentTarget.value)}
              placeholder="production"
            />
          </Field>

          <Field
            label="Allowed networks"
            hint="CIDR ranges this key may connect from. Empty allows any — restrict it for production keys."
          >
            <TagInput
              value={cidrs}
              onValueChange={setCidrs}
              validate={(value) =>
                /^\d{1,3}(\.\d{1,3}){3}\/\d{1,2}$/.test(value) ? null : 'Enter a CIDR range like 10.0.0.0/8'
              }
              placeholder="203.0.113.0/24"
            />
          </Field>

          <Field label="Maximum devices" hint="How many devices may pair with this key.">
            <Input
              type="number"
              min={1}
              max={10}
              value={maxDevices}
              onChange={(event) => setMaxDevices(event.currentTarget.value)}
              className="w-24"
            />
          </Field>
        </div>
      </Modal>

      {/* One-time reveal */}
      <ApiKeyReveal open={freshKey !== null} apiKey={freshKey} onClose={() => setFreshKey(null)} />

      {/* Revoke */}
      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null)
        }}
        title={`Revoke "${revokeTarget?.label ?? ''}"?`}
        description="Devices using this key will stop being able to connect or renew."
        confirmLabel="Revoke key"
        destructive
        loading={revoke.isPending}
        onConfirm={() => revokeTarget && revoke.mutate(revokeTarget.id)}
      />
    </div>
  )
}
